<template>
	<div class="flex">
		<DropdownStatus
			:options="notificationOptions"
			id="notification-dropdown"
			optionLabel="label"
			optionValue="value"
			:placeholder="null"
			@change="onNotificationSelect"
			class="notification-dropdown rounded inline-flex items-center border !border-success !focus:border-success !active:border-success !outline-success"
		>
			<template #value>
				<i class="tabler:bell-ringing !p-0 !m-0 text-size-xl color-success block" v-tooltip.bottom="'Available Notifications'"></i>
			</template>

			<template #dropdownicon></template>

			<template #option="slotProps">
				<EditButton
					:id="callout.identifier"
					:type="slotProps.option.value"
					:overrideURL="slotProps.option.url"
					:buttonLabel="slotProps.option.label"
					:headerTitle="slotProps.option.headerTitle"
					:icon="slotProps.option.icon"
					@click="console.log('Going to:', slotProps.option.url)"
					:class="['w-full text-right gap-2', slotProps.option.class]"
				/>
			</template>
		</DropdownStatus>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import EditButton from "@/components/EditButton.vue";
import type { configCollectionMode, LanguageCodes } from '@/types';

const props = defineProps<{
	callout: any;
	AppConfig: any;
	configcollection: configCollectionMode | null
	selectedLanguage?: LanguageCodes
	hiddeNotifyReviewers?: boolean;
	hiddeNotifyLanguageReviewers?: boolean;
	hiddeNotifyEditorPublisher?: boolean;
	hiddeNotifyAudience?: boolean;
	hiddeProductionTeam?: boolean;
}>();

const ReviewersURL = computed(() => {
  const baseUrl = props.AppConfig.urls.notification?.ReviewersURL ?? ""
  const id = props.callout.identifier ?? ""
  const packageLng = props.selectedLanguage ?? "en"
  const sourcereqid = props.AppConfig.PAGE_REQ_ID ?? ""
  const notificationType = 'main_author-reviewer'
  const separator = baseUrl.includes("?") ? "&" : "?"
  const fullUrl = `${baseUrl}${separator}apprcss=SATnotification&notificationType=${notificationType}&packageid=${id}&packageLng=${packageLng}&sourcereqid=${sourcereqid}`
  return fullUrl
})
const LanguageReviewersURL = computed(() => {
  const baseUrl = props.AppConfig.urls.notification?.LanguageReviewersURL ?? ""
  const id = props.callout.identifier ?? ""
  const packageLng = props.selectedLanguage ?? "en"
  const notificationType = 'main_author-reviewer_language'
  const sourcereqid = props.AppConfig.PAGE_REQ_ID ?? ""
  const separator = baseUrl.includes("?") ? "&" : "?"
  const fullUrl = `${baseUrl}${separator}apprcss=SATnotification&notificationType=${notificationType}&packageid=${id}&packageLng=${packageLng}&sourcereqid=${sourcereqid}`
  return fullUrl
})
const EditorPublisherURL = computed(() => {
  const baseUrl = props.AppConfig.urls.notification?.EditorPublisherURL ?? ""
  const id = props.callout.identifier ?? ""
  const packageLng = props.selectedLanguage ?? "en"
  const notificationType = 'main_author-editor_publisher'
  const sourcereqid = props.AppConfig.PAGE_REQ_ID ?? ""
  const separator = baseUrl.includes("?") ? "&" : "?"
  const fullUrl = `${baseUrl}${separator}apprcss=SATnotification&notificationType=${notificationType}&packageid=${id}&packageLng=${packageLng}&sourcereqid=${sourcereqid}` 
  return fullUrl
})
const AudienceURL = computed(() => {
  const baseUrl = props.AppConfig.urls.notification?.AudienceURL ?? ""
  const id = props.callout.identifier ?? ""
  const packageLng = props.selectedLanguage ?? "en"
  const notificationType = 'editor_publisher-audience'
  const sourcereqid = props.AppConfig.PAGE_REQ_ID ?? ""
  const separator = baseUrl.includes("?") ? "&" : "?"
  const fullUrl = `${baseUrl}${separator}apprcss=SATnotification&notificationType=${notificationType}&packageid=${id}&packageLng=${packageLng}&sourcereqid=${sourcereqid}`
  return fullUrl
})
const ProductionTeamURL = computed(() => {
  const baseUrl = props.AppConfig.urls.notification?.ProductionTeamURL ?? "";
  const id = props.callout.identifier ?? "";
  const packageLng = props.selectedLanguage ?? "en";
  const notificationType =
    props.configcollection?.id === "language_review"
      ? "reviewer_language-production_team"
      : "reviewer-production_team";

  const sourcereqid = props.AppConfig.PAGE_REQ_ID ?? "";
  const separator = baseUrl.includes("?") ? "&" : "?";

  const fullUrl = `${baseUrl}${separator}apprcss=SATnotification&notificationType=${notificationType}&packageid=${id}&packageLng=${packageLng}&sourcereqid=${sourcereqid}`;
  return fullUrl;
});


const notificationOptions = computed(() => {
	const opts: { label: string; url: string; value: string; class: string, icon?: string, headerTitle?: string }[] = [];

	if (props.hiddeNotifyReviewers && props.AppConfig.urls.notification?.ReviewersURL) {
		opts.push({
			label: 'Notify Reviewers',
			headerTitle: 'Notify Reviewers',
			value: 'notification',
			url: ReviewersURL.value,
			icon:'tabler:mail',
			class: 'rounded items-center text-center font-sans text-md hover:text-primary-500 !text-dark',
		});
	}
	if (props.hiddeNotifyLanguageReviewers && props.AppConfig.urls.notification?.LanguageReviewersURL) {
		opts.push({
			label: 'Notify Language Reviewers',
			headerTitle: 'Notify Language Reviewers',
			value: 'notification',
			url: LanguageReviewersURL.value,
			icon:"tabler:globe",
			class: 'rounded items-center text-center font-sans text-md hover:text-primary-500 !text-dark',
		});
	}
	if (props.hiddeNotifyEditorPublisher && props.AppConfig.urls.notification?.EditorPublisherURL) {
		opts.push({
			label: 'Notify Editor-Publisher',
			headerTitle: 'Notify Editor-Publisher',
			value: 'notification',
			url: EditorPublisherURL.value,
			icon:"tabler:user-edit",
			class: 'rounded items-center text-center font-sans text-md hover:text-primary-500 !text-dark',
		});
	}
	if (props.hiddeNotifyAudience && props.AppConfig.urls.notification?.AudienceURL) {
		opts.push({
			label: 'Notify Audiences',
			headerTitle: 'Notify Audiences',
			value: 'notification',
			url: AudienceURL.value,
			icon:"tabler:users",
			class: 'rounded items-center text-center font-sans text-md hover:text-primary-500 !text-dark',
		});
	}
	if (props.hiddeProductionTeam && props.AppConfig.urls.notification?.ProductionTeamURL) {
		opts.push({
			label: 'Notify Production Team',
			headerTitle: 'Notify Production Team',
			value: 'notification',
			url: ProductionTeamURL.value,
			icon:"tabler:user-check",
			class: 'rounded items-center text-center font-sans text-md hover:text-primary-500 !text-dark',
		});
	}

	return opts;
});

function onNotificationSelect(option: any) {
	if (option?.url) {
		window.location.href = option.url;
	}
}
onMounted(() => {
//   console.log('ReviewersURL:', ReviewersURL.value)
//   console.log('LanguageReviewersURL:', LanguageReviewersURL.value)
//   console.log('EditorPublisherURL:', EditorPublisherURL.value)
//   console.log('AudienceURL:', AudienceURL.value)
//   console.log('ProductionTeamURL:', ProductionTeamURL.value)
})

</script>

<style scoped>
#notification-dropdown .p-dropdown-trigger {
	display: none !important;
}

#notification-dropdown .p-inputtext {
	padding-left: 0.5rem !important;
	padding-right: 0 !important;
	padding-top: 0.5rem !important;
	padding-bottom: 0.5rem !important;
}
ul#notification-dropdown_list li .p-dropdown-item {
	padding: 0rem !important;
}
</style>
